/**
 * matrix-table-manifest.json 로더 — Zod 검증 + 모듈 스코프 캐시
 * (Vite·Jest 공통: JSON 정적 import — node:fs 미사용)
 */

import { z } from 'zod';
import type { TableManifest, TableManifestSubdomain } from '@shared/types/table-manifest';
import manifestJson from '../../../docs/data/matrix-table-manifest.json';

const FIELD_GROUP_VALUES = [
  'basic',
  'custom',
  'value',
  'impact',
  'relation',
  'system',
  'finance_link',
  'reference_link',
  'trigger_link',
  'data_mirror',
  'state_signal',
] as const;

export type ManifestFieldGroup = (typeof FIELD_GROUP_VALUES)[number];
export { FIELD_GROUP_VALUES };

const RELATION_TYPE_VALUES = ['F', 'R', 'T', 'D', 'E'] as const;

const fieldGroupSchema = z.enum(FIELD_GROUP_VALUES);
const relationTypeSchema = z.enum(RELATION_TYPE_VALUES);

const relationHintSchema = z.object({
  target: z.string().min(1),
  fieldKey: z.string().min(1),
  type: relationTypeSchema,
});

const navigationLinkSchema = z.object({
  target: z.string().min(1),
  label: z.string().min(1),
  via: relationTypeSchema,
});

const tableManifestSubdomainSchema = z.object({
  subdomain: z.string().min(1),
  columnGroups: z.array(fieldGroupSchema).min(1),
  defaultVisible: z.array(z.string().min(1)),
  groupOrder: z.array(fieldGroupSchema).min(1),
  rowActions: z.array(z.string().min(1)),
  workflows: z.array(z.string().min(1)),
  relationHints: z.array(relationHintSchema),
  domainColumns: z.array(z.string().min(1)),
  navigation: z.object({
    crossDomain: z.array(navigationLinkSchema),
  }),
});

export const tableManifestSchema = z
  .object({
    version: z.string().min(1),
    schema: z.literal('1.0'),
    subdomains: z.array(tableManifestSubdomainSchema).min(1),
  })
  .superRefine((manifest, ctx) => {
    const seen = new Set<string>();
    for (const entry of manifest.subdomains) {
      if (seen.has(entry.subdomain)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate subdomain: ${entry.subdomain}`,
          path: ['subdomains'],
        });
      }
      seen.add(entry.subdomain);
    }
  });

const MANIFEST_JSON = manifestJson;

let cachedManifest: TableManifest | null = null;

function readManifestJson(): unknown {
  return MANIFEST_JSON;
}

/**
 * matrix-table-manifest.json 을 로드하고 Zod 스키마로 검증한다.
 * 모듈 스코프 LRU(단일 슬롯) 캐시를 사용한다.
 */
export function loadTableManifest(): TableManifest {
  if (cachedManifest) {
    return cachedManifest;
  }
  const parsed = tableManifestSchema.parse(readManifestJson());
  cachedManifest = parsed as TableManifest;
  return cachedManifest;
}

/** 테스트·핫 리로드용 캐시 초기화 */
export function clearTableManifestCache(): void {
  cachedManifest = null;
}

export function getManifestForSubdomain(subdomainId: string): TableManifestSubdomain | undefined {
  return loadTableManifest().subdomains.find((entry) => entry.subdomain === subdomainId);
}
